import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Divider, Grid, IconButton, Link, Typography, useTheme } from '@mui/material';
import { get as _get } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { useModalVisibility } from '../../../app/hooks/useModalVisibility';
import {
  getAuthKey,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getUserData,
} from '../../../app/store/combinedSelectors';
import {
  resetAvailableProgressStagesState,
  resetEligibleServiceState,
  resetSelectedProvidersState,
} from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn, noVehiclesFoundText, viewDetailsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { setupPostFnol } from '../../../services/SetupPostFnol.service';
import { GenericProductType } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import { getClaimVehicles, Vehicle } from '../../../utils/Vehicle-Util';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ModalLinearActivityIndicator from '../ModalLinearActivityIndicator/ModalLinearActivityIndicator';

const VEHICLES_PER_PAGE = 10;

const normalizeClaimNumber = (claimNumber?: string) => (claimNumber ?? '').toString().replace(/-/g, '');

export default function Vehicles() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const claimNumber = _get(state, 'claimNumber', '') as string;

  // Source-agnostic resolver: pulls vehicles from the claims slice (overview path) or the
  // claimDetail slice (direct claim-detail path) and normalizes them into one canonical shape.
  const vehicles: Vehicle[] = useAppSelector((reduxState) => getClaimVehicles(reduxState, claimNumber));

  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const customerData = useSelector(getUserData);
  const opco = useSelector(getServicingOpCo);

  const [page, setPage] = React.useState(0);
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const setModalVisibility = useModalVisibility(setModalLoadingOpen);
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);

  const totalPages = Math.max(1, Math.ceil(vehicles.length / VEHICLES_PER_PAGE));
  const pagedVehicles = vehicles.slice(page * VEHICLES_PER_PAGE, page * VEHICLES_PER_PAGE + VEHICLES_PER_PAGE);

  // If we arrived without a claim (e.g. hard refresh), send the user back to the claims list.
  React.useEffect(() => {
    if (!claimNumber) {
      navigate('/claims-overview');
    }
  }, [claimNumber, navigate]);

  function goToClaims() {
    navigate('/claims-overview', { state: { isClaimRefreshNeeded: true } });
  }

  async function viewDetails() {
    // Reset eligibility / progress state, mirroring the claims-overview "View details" flow.
    dispatch(resetEligibleServiceState());
    dispatch(resetAvailableProgressStagesState());
    dispatch(resetSelectedProvidersState());

    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    setShowErrorBanner(false);
    setModalVisibility(true);
    const unformattedClaimNumber = normalizeClaimNumber(claimNumber);
    const successfulSetup = await setupPostFnol(
      customerData,
      unformattedClaimNumber,
      urlKey,
      sessionId,
      authKey,
      true,
      opco,
      'vehicles'
    );
    setModalVisibility(false);
    if (
      successfulSetup === GenericProductType.Auto ||
      successfulSetup === GenericProductType.Home ||
      successfulSetup === GenericProductType.CommercialAuto ||
      successfulSetup === GenericProductType.CommercialProperty
    ) {
      navigate('/claim-detail');
    } else {
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_V2_CLAIM_DETAIL_FAIL_ERROR,
        unformattedClaimNumber,
        successfulSetup
      );
      setShowErrorBanner(true);
    }
  }

  return (
    <Box>
      {showErrorBanner && <ErrorBanner claimNumber={claimNumber}></ErrorBanner>}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid container sx={{ marginTop: '1rem' }}>
        <Grid item xs={12}>
          <Typography sx={theme.custom.styles.titleType3}>Vehicles</Typography>
        </Grid>

        <Grid item xs={12} sx={{ marginTop: '1.5rem' }}>
          {vehicles.length === 0 ? (
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              {noVehiclesFoundText}
            </Typography>
          ) : (
            pagedVehicles.map((vehicle: Vehicle, index: number) => (
              <Box key={`${vehicle.vin}-${index}`} sx={{ marginBottom: '1.5rem' }}>
                <Typography sx={{ ...theme.custom.styles.titleType4 }}>
                  {`${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim()}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                  VIN: {vehicle.vin}
                </Typography>
              </Box>
            ))
          )}
        </Grid>

        {vehicles.length > VEHICLES_PER_PAGE && (
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              Page {page + 1} of {totalPages}
            </Typography>
            <Box>
              <IconButton
                aria-label="Previous page"
                disabled={page === 0}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                aria-label="Next page"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sx={{ marginTop: '1.5rem' }}>
          <Divider />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            marginTop: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Link
            component="button"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}
            onClick={goToClaims}
          >
            <ChevronLeftIcon fontSize="small" />
            {goToClaimsBtn}
          </Link>

          <Button
            onClick={viewDetails}
            sx={{
              ...theme.custom.styles.genericPrimaryButton,
              '&.MuiButtonBase-root:hover': {
                bgcolor: theme.custom.colors.primaryButtonHoverColor,
              },
            }}
          >
            {viewDetailsBtn}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
